import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { API_ENDPOINTS } from '../config';

const CartContext = createContext();

export function useCart() {
  return useContext(CartContext);
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const { firebaseUser } = useAuth();

  useEffect(() => {
    if (firebaseUser) {
      fetchCart();
    } else {
      setCart(null);
      setLoading(false);
    }
  }, [firebaseUser]);

  const fetchCart = async () => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.get(API_ENDPOINTS.cart, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (productId, variant, quantity) => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.post(
        `${API_ENDPOINTS.cart}/items`,
        { productId, variant, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      return false;
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.patch(
        `${API_ENDPOINTS.cart}/items/${itemId}`,
        { quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Error updating quantity:', error);
      return false;
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.delete(
        `${API_ENDPOINTS.cart}/items/${itemId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Error removing from cart:', error);
      return false;
    }
  };

  const clearCart = async () => {
    try {
      const token = await firebaseUser.getIdToken();
      const response = await axios.delete(API_ENDPOINTS.cart, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      return true;
    } catch (error) {
      console.error('Error clearing cart:', error);
      return false;
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
} 